import axios from "axios";
import { API_DETAILS } from "../App";

/*
 * The admin analytics endpoints.
 *
 * Every one of these returns counts, sums and averages. None of them returns a
 * patient — no name, no email, no id — so nothing reached through this file can
 * put a person's record on a chart. The server enforces that; this comment is
 * here so a future change that starts returning rows is noticed as the change it
 * would be.
 *
 * All of them are admin-only on the server. An assistant, who can otherwise use
 * the admin console, gets a 403.
 */

const ANALYTICS_PATH = "/admin/analytics";

const analyticsUrl = (path) =>
  API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + ANALYTICS_PATH + path;

/**
 * The windows the server understands. Anything else it silently treats as 30
 * days, so the screen and the server cannot disagree about what is on display —
 * but the caption always comes from the server's own answer, not from this list.
 */
export const RANGES = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "All time" },
];

export const DEFAULT_RANGE = "30d";

const get = async (path, range) => {
  const res = await axios.get(analyticsUrl(path), { params: { range } });
  return res.data;
};

export const fetchOverview = (range) => get("/overview", range);
export const fetchMembership = (range) => get("/membership", range);
export const fetchAccess = (range) => get("/access", range);
export const fetchOutcomes = (range) => get("/outcomes", range);

/* ------------------------------------------------------------------ formatting
 *
 * Every number the server sends can be null, and null means "there was nothing
 * to measure" rather than zero. A renewal rate of null is a business with no
 * subscriptions that have ended yet; a renewal rate of 0 is a business whose
 * subscriptions all lapsed. Showing the first as "0%" would be a lie, so these
 * helpers render null as a dash and nothing collapses the two.
 */

export const EMPTY = "—";

/** Money arrives as cents, exactly as Stripe charged it. */
export const money = (cents) => {
  if (cents === null || cents === undefined) return EMPTY;
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

export const count = (value) => {
  if (value === null || value === undefined) return EMPTY;
  return Number(value).toLocaleString("en-US");
};

export const percent = (value) => {
  if (value === null || value === undefined) return EMPTY;
  return `${value}%`;
};

export const decimal = (value, unit = "") => {
  if (value === null || value === undefined) return EMPTY;
  return unit ? `${value} ${unit}` : String(value);
};

/**
 * Adds numbers the server sent, or gives back null if any of them is missing.
 *
 * <p>Needed because arithmetic silently breaks the rule the rest of this file
 * keeps. `null + 5` is 5, so a missing count quietly becomes a zero and
 * understates a denominator; `undefined + 5` is NaN, and `count(NaN)` renders
 * the literal text "NaN" into a card. Either way the screen states something
 * the server did not.
 */
export const sum = (...values) => {
  let total = 0;
  for (const value of values) {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    total += value;
  }
  return total;
};

/** "1 Sep – 30 Sep 2026", or "All time" where there is no start. */
export const rangeCaption = (range) => {
  if (!range) return "";
  if (range.key === "all") return range.label;
  const opts = { month: "short", day: "numeric" };
  const from = new Date(`${range.from}T12:00:00`);
  const to = new Date(`${range.to}T12:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return range.label;
  return `${from.toLocaleDateString("en-US", opts)} – ${to.toLocaleDateString("en-US", {
    ...opts,
    year: "numeric",
  })}`;
};

/**
 * A chart colour, read from the design system rather than written here.
 *
 * recharts needs a real colour string, not a CSS variable, so the token has to be
 * resolved in JavaScript. Reading it from the document means the palette still
 * lives in one file and a change there moves the charts too.
 *
 * <p>The fallback is a neutral grey rather than a copy of each token's hex. A
 * duplicated palette in JS is invisible to stylelint and drifts —
 * DESIGN_SYSTEM.md §11.5 blames exactly that for the four near-miss variants of
 * Mint that already exist in this codebase. This cannot drift: if a token ever
 * fails to resolve the chart goes grey, which is visibly wrong and gets fixed,
 * instead of quietly rendering a colour that used to be right.
 *
 * <p>In practice it never fires. The stylesheet is in the document before first
 * render in both dev and production builds; the only caller that sees the
 * fallback is jsdom under test, where colours are not asserted.
 */
const UNRESOLVED = "#767676";

export const token = (name) => {
  if (typeof window === "undefined" || !window.getComputedStyle) return UNRESOLVED;
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name);
  return (value || "").trim() || UNRESOLVED;
};
